import React, { useState, useRef, useEffect, ChangeEvent, KeyboardEvent, useCallback, useContext } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  alpha,
} from '@material-ui/core';
import { faSparkles, faCommentAltPlus } from '@fortawesome/pro-regular-svg-icons';
import { USERS_AND_ROLES_Z_INDEX } from '../utils/layers';
import { colors } from '../styles/theme';
import { gql, useApolloClient } from '@apollo/client';
import { aiActiveState, aiLoadingState, aiMessagesState } from '../recoil/atoms/aiMessages';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAlert } from '../contexts/AlertModalContext';
import _ from 'lodash';
import { AssetsDashboardContext } from '../contexts/assetsDashboardContext';
import { faArrowUp, faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import { faUndo } from '@fortawesome/pro-light-svg-icons';

// --- Type Definitions ---
export interface AiMessage {
  id: number;
  sender: 'user' | 'bot' | 'system';
  text: string;
}

export const aiChatWidth = '470px';

// --- Styles ---
const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    container: {
      height: '100vh',
      zIndex: USERS_AND_ROLES_Z_INDEX + 1,
      position: 'fixed',
      width: aiChatWidth,
      // height: '100vh',
      borderRight: `1px solid ${colors.black10}`,
      backgroundColor: colors.white,
      padding: '0',
      overflow: 'auto',
      left: 70,
      display: 'flex',
      flexDirection: 'column',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    chatWindow: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
    },
    header: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: 24,
      paddingBottom: 22,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    messageArea: {
      flexGrow: 1,
      overflowY: 'auto',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    messageRow: {
      display: 'flex',
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    rowRight: {
      justifyContent: 'flex-end',
      display: 'flex',
      flexDirection: 'column',
    },
    rowLeft: {
      justifyContent: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
    },
    bubble: {
      padding: theme.spacing(2),
      maxWidth: '75%',
      borderRadius: theme.spacing(2),
      wordWrap: 'break-word',
      display: 'inline-flex',
    },
    bubbleUser: {
      backgroundColor: theme.palette.primary.main,
      color:
        parseInt(theme.palette.primary.main.substring(1, 2), 16) * 0.299 +
          parseInt(theme.palette.primary.main.substring(3, 2), 16) * 0.587 +
          parseInt(theme.palette.primary.main.substring(5, 2), 16) * 0.114 <
        186
          ? '#fff'
          : '#000',
      borderTopRightRadius: 0,
      marginLeft: 'auto',
    },
    bubbleBot: {
      backgroundColor: colors.black05,
      color: theme.palette.text.primary,
      borderTopLeftRadius: 0,
      marginRight: 'auto',
    },
    bubbleSystem: {
      backgroundColor: '#FFF4F4',
      color: colors.pureRed,
      borderTopLeftRadius: 0,
      marginRight: 'auto',
    },
    bubbleRetry: {
      backgroundColor: '#FCFCFC',
      color: theme.palette.primary.main,
      marginRight: 'auto',
      padding: '5px 15px',
      marginTop: 5,
      cursor: 'pointer',
      alignItems: 'center',
      border: `1px solid ${colors.black10}`,
    },
    avatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
    inputArea: {
      padding: '20px 24px',
      backgroundColor: theme.palette.background.paper,
      width: '90%',
    },
    inputContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: '6.5px 5px',
      borderRadius: 12,
      marginTop: -6,
      border: `2px solid ${colors.black10}`,
      '&:focus-within': {
        boxShadow: `0 0 5px 5px ${theme.palette.primary.main}33`,
        border: `2px solid ${theme.palette.primary.main}`,
      },
    },
    sendButton: {
      marginLeft: theme.spacing(1),
      backgroundColor: theme.palette.primary.main,
      marginRight: 8,
      color: '#fff',
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}BB`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
      },
    },
    betaContainer: {
      position: 'absolute',
      bottom: 24,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
    },
    betaTag: {
      marginLeft: 8,
      padding: '1px 6px',
      color: theme.palette.primary.main,
      border: `0.1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      backgroundColor: '#F7F7F7',
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 500,
    },
    titleLabel: {
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
    },
    actionIcon: {
      color: colors.black54,
      padding: 10,
      width: 16,
      height: 16,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: colors.black05,
        borderRadius: '50%',
      },
    },
    chatIconInitial: {
      color: theme.palette.primary.main,
      marginRight: 10,
      marginBottom: 10,
      padding: 10,
      borderRadius: '50%',
      border: `1px solid ${theme.palette.primary.main}`,
      fontSize: 24,
    },
    chatIcon: {
      color: theme.palette.primary.main,
      marginRight: 10,
      padding: 8,
      borderRadius: '50%',
      border: `1px solid ${theme.palette.primary.main}`,
      fontSize: 20,
    },
    initialWrapper: {
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      transform: 'translateY(-100%)',
    },
    chatInitialScreen: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: '0px 16px',
    },
    name: {
      fontSize: 12,
      color: colors.black75,
    },
    ellipsis: {
      width: 10,
      height: 10,
      marginRight: 5,
      color: colors.black54,
    },
  });
});

const AiAnalysis: React.FC = () => {
  const classes = useStyles();

  const client = useApolloClient();
  const [input, setInput] = useState<string>('');
  const [isLoading, setIsLoading] = useRecoilState(aiLoadingState);
  const [messages, setMessages] = useRecoilState(aiMessagesState);
  const [animationState, setAnimationState] = useState(0);
  const setAiActive = useSetRecoilState(aiActiveState);
  const { openConfirmation } = useAlert();
  const loadingRef = useRef(isLoading);
  const { isMobileView } = useContext(AssetsDashboardContext);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const animateLoad = () => {
    if (loadingRef.current) {
      setAnimationState(s => (s + 1) % 4);
      setTimeout(animateLoad, 500);
    }
  };

  const handleSendMessage = async (resend?: boolean) => {
    if (!resend && !input.trim()) return;

    let lastUserIndex = -1;
    for (lastUserIndex = messages.length - 1; lastUserIndex >= 0; lastUserIndex--) {
      if (messages[lastUserIndex]?.sender === 'user') {
        break;
      }
    }
    const userMessage: AiMessage = {
      id: Date.now(),
      sender: 'user',
      text: resend ? messages[lastUserIndex]?.text || '' : input,
    };
    const messageHistory = messages.filter(
      m => m.sender !== 'system' && (!resend || m.id !== messages[lastUserIndex]?.id)
    );
    setInput('');
    setMessages([...messageHistory, userMessage]);
    setIsLoading(true);
    loadingRef.current = true;
    setTimeout(() => {
      animateLoad();
      client
        .query<{ aiAnalysis: string }>({
          query: gql`
            query MyQuery($value: String!, $history: [String!]!) {
              aiAnalysis(message: $value, history: $history)
            }
          `,
          fetchPolicy: 'no-cache',
          variables: {
            value: userMessage.text,
            history: messageHistory
              .filter((_m, i, a) => a.length - i <= 20) //Limit visible history to the last 10 user/bot message pairs
              .filter((m, i) => m.sender === 'bot' || messages[i + 1]?.sender === 'bot')
              .map(h => h.text),
          },
        })
        .then(result => {
          const botResponse: AiMessage = {
            id: Date.now() + 1,
            sender: 'bot',
            text: result.data.aiAnalysis,
          };

          setMessages(prev => [...prev, botResponse]);
        })
        .catch(error => {
          console.error('Error:', error);
          setMessages(prev => [
            ...prev,
            {
              id: Date.now() + 1,
              sender: 'system',
              text: 'Unable to complete the request at this time. Retry the request or enter a new prompt.',
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
          loadingRef.current = false;
        });
    }, 100);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = useCallback(async () => {
    const status = await openConfirmation({
      title: `Start a New Chat`,
      question: `This will clear your current session and open a new chat. Do you want to continue?`,
      cancellationText: 'Cancel',
      confirmationText: 'Continue',
      type: 'confirmation',
    });
    if (status === 'confirm') {
      setMessages([]);
    }
  }, [openConfirmation]);

  return (
    <Container maxWidth={false} className={classes.container} style={isMobileView ? { width: '100vw', left: 0 } : {}}>
      {!messages.length && (
        <FontAwesomeIcon
          className={classes.actionIcon}
          onClick={() => setAiActive(false)}
          style={{ marginLeft: `calc(${isMobileView ? '100vw' : aiChatWidth} - 50px)`, marginTop: 15 }}
          icon={faXmarkLarge}
        />
      )}
      <div
        className={classes.chatWindow}
        style={
          !messages.length
            ? { display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center' }
            : {}
        }>
        {/* --- Header --- */}
        {!!messages.length && (
          <Box className={classes.header}>
            <Box>
              <Typography className={classes.titleLabel} style={{ fontSize: '1.25rem' }}>
                <FontAwesomeIcon className={classes.chatIcon} icon={faSparkles} />
                Ask AI
                <Typography className={classes.betaTag}>Beta</Typography>
              </Typography>
            </Box>
            <span style={{ display: 'flex', alignItems: 'center', position: 'relative', right: 0 }}>
              <FontAwesomeIcon
                className={classes.actionIcon}
                style={{ width: 20, height: 20 }}
                onClick={handleNewChat}
                icon={faCommentAltPlus}
              />
              <FontAwesomeIcon className={classes.actionIcon} onClick={() => setAiActive(false)} icon={faXmarkLarge} />
            </span>
          </Box>
        )}

        {/* --- Message List --- */}
        {!!messages.length && (
          <div className={classes.messageArea}>
            {messages.map(msg => {
              const isUser = msg.sender === 'user';
              return (
                <div key={msg.id} className={`${classes.messageRow} ${isUser ? classes.rowRight : classes.rowLeft}`}>
                  <div style={{ width: '100%' }} className={isUser ? classes.rowRight : classes.rowLeft}>
                    {!isUser && <div className={classes.name}>AI</div>}
                    {isUser && (
                      <div className={classes.name} style={{ textAlign: 'right' }}>
                        You
                      </div>
                    )}

                    <div
                      className={`${classes.bubble} ${
                        isUser ? classes.bubbleUser : msg.sender === 'bot' ? classes.bubbleBot : classes.bubbleSystem
                      }`}>
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {msg.text}
                      </Typography>
                    </div>
                    {msg.sender === 'system' && (
                      <div
                        onClick={() => handleSendMessage(true)}
                        className={`${classes.bubble} ${classes.bubbleRetry}`}>
                        <FontAwesomeIcon style={{ fontSize: 12, paddingRight: 4 }} icon={faUndo} />
                        <Typography style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>Resend</Typography>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Loading Indicator */}
            {isLoading && (
              <div className={`${classes.messageRow} ${classes.rowLeft}`}>
                <div className={classes.name}>AI</div>
                <Paper
                  style={{ width: 45, height: 10 }}
                  elevation={0}
                  className={`${classes.bubble} ${classes.bubbleBot}`}>
                  {animationState >= 1 && (
                    <FontAwesomeIcon className={classes.ellipsis} icon="circle"></FontAwesomeIcon>
                  )}
                  {animationState >= 2 && (
                    <FontAwesomeIcon className={classes.ellipsis} icon="circle"></FontAwesomeIcon>
                  )}
                  {animationState >= 3 && (
                    <FontAwesomeIcon className={classes.ellipsis} icon="circle"></FontAwesomeIcon>
                  )}
                </Paper>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        <div className={`${!messages.length && classes.initialWrapper}`}>
          {!messages.length && (
            <div className={classes.chatInitialScreen}>
              <FontAwesomeIcon className={classes.chatIconInitial} icon={faSparkles} />
              <Typography className={classes.titleLabel} style={{ fontSize: '1.5rem' }}>
                What would you like to know?
              </Typography>
              <p style={{ color: colors.black54, textAlign: 'center', marginTop: 0, marginBottom: 5, fontSize: 15 }}>
                Explore questions, find answers, and uncover useful insights in a simple and intuitive way with AI.
              </p>
            </div>
          )}

          {/* --- Input Area --- */}
          <div
            className={classes.inputArea}
            style={{ borderTop: !!messages.length ? `1px solid ${colors.black10}` : 'none' }}>
            <div className={classes.inputContainer}>
              <TextField
                fullWidth
                variant="standard"
                placeholder="Type your question here"
                multiline
                maxRows={4}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                size="small"
                InputProps={{
                  style: {
                    borderRadius: 12,
                    marginLeft: 10,
                    marginRight: 10,
                    paddingRight: 5,
                    paddingBottom: 3,
                  },
                  disableUnderline: true,
                }}
              />
              <IconButton
                className={classes.sendButton}
                onClick={() => handleSendMessage(false)}
                disabled={!input.trim() || isLoading}
                style={{ width: 32, height: 32 }}>
                <FontAwesomeIcon style={{ fontSize: '14px', color: colors.white }} icon={faArrowUp} />
              </IconButton>
            </div>
          </div>
        </div>
        {!!messages.length && (
          <Typography
            variant="caption"
            display="block"
            align="center"
            style={{ marginBottom: 20, color: colors.black45 }}>
            Beta unlocks advanced AI features; always verify results.
          </Typography>
        )}

        {/* --- Beta Indicator --- */}
        {!messages.length && (
          <Box className={classes.betaContainer}>
            <Box style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
              <Typography className={classes.titleLabel} style={{ fontSize: '1.1rem' }}>
                Ask AI
                <Typography className={classes.betaTag}>Beta</Typography>
              </Typography>
              <Typography variant="caption" display="block" align="center" style={{ color: colors.black45 }}>
                Beta unlocks advanced AI features; always verify results.
              </Typography>
            </Box>
          </Box>
        )}
      </div>
    </Container>
  );
};

export default AiAnalysis;
